import React, {useState, useRef} from "react";
import styled from "styled-components";
import {Link} from "react-router";

import Summary from "./summary";
import Context from "./context";
import Inputs from "./inputs";
import Variables from "./variables";
import OutputWithTests from "../../../../common_components/flowmaster/output_with_tests";
import Actions from "./actions";
import ErrorSummary from "./error_summary";

import MoreVertIcon from "@material-ui/icons/MoreVert";
import Checkbox from "@material-ui/core/Checkbox";
import formatDateToLocalISOString from "routes/llm_run/utils/format_date_local_iso_string";
import {
  Button,
  CircularProgress,
  FormControlLabel,
  IconButton,
  Menu,
  MenuItem,
} from "@material-ui/core";
import {
  EnhancedLlmRun,
  llmRunFilterKeys,
  LlmRunFilters,
} from "common/flowmaster/types/llm_run";
import {TestId} from "common/flowmaster/types/test";
import {ActionDefinitionWithType} from "common/flowmaster/types/task_config";
import {DataKey, Prompt} from "common/flowmaster/types/workflow";
import {OrganisationId} from "common/types/organisation";

const Container = styled.div`
  padding: 1em;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  position: sticky;
  top: 0;
  background-color: white;
  z-index: 10000;
`;

const TitleSection = styled.div`
  display: flex;
  align-items: center;
  flex: 1;
`;

const WorkflowName = styled.h2`
  flex: 1;
  text-align: center;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
`;

const PlainLink = styled(Link)`
  text-decoration: none;
  color: black;
  &:hover {
    text-decoration: underline #ddd;
  }
`;

const BackLink = styled(PlainLink)`
  font-size: 1.2em;
  margin: 0 1em;
`;

type Props = {
  organisationId: OrganisationId;
  llmRun: EnhancedLlmRun;
  actionDefinitions: ActionDefinitionWithType[];
  prompts: Prompt[];
  showInternalActions: boolean;
  onInternalActionsToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  showActionDetail: boolean;
  onShowActionDetailToggle: (
    event: React.ChangeEvent<HTMLInputElement>,
  ) => void;
  isDeveloperMode: boolean;
  onDeveloperModeToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onAddTest: (dataKey: DataKey, key: string) => void;
  onUpdateTest: (
    testId: TestId,
    testType: string,
    value: Record<string, unknown>,
  ) => void;
  onDeleteTest: (testId: TestId) => void;
  rerunTests: () => void;
  rerunLlmRun: () => void;
  getTestResults: (dataKey: DataKey, key: string) => void;
  fetchLlmRun: () => void;
  autoRefreshEnabled: boolean;
  onAutoRefreshToggle: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isRefreshing: boolean;
};

export default function llmRunDetailComponent({
  organisationId,
  llmRun,
  actionDefinitions,
  prompts,
  showInternalActions,
  onInternalActionsToggle,
  showActionDetail,
  onShowActionDetailToggle,
  isDeveloperMode,
  onDeveloperModeToggle,
  onAddTest,
  onUpdateTest,
  onDeleteTest,
  rerunTests,
  rerunLlmRun,
  getTestResults,
  fetchLlmRun,
  autoRefreshEnabled,
  onAutoRefreshToggle,
  isRefreshing,
}: Props) {
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);

  const handleMenuClick = () => {
    setMenuOpen(prevOpen => !prevOpen);
  };

  const handleMenuClose = (event: React.MouseEvent<Document, MouseEvent>) => {
    if (
      anchorRef.current &&
      anchorRef.current.contains(event.target as HTMLElement)
    ) {
      return;
    }
    setMenuOpen(false);
  };

  const handleRerunLlmRun = () => {
    rerunLlmRun();
    setMenuOpen(false);
  };

  const handleRerunTests = () => {
    rerunTests();
    setMenuOpen(false);
  };

  const getBackLinkUrl = location => {
    const baseUrl = location.pathname.replace(/([^/]+?\/detail)/, "list");
    const params = new URLSearchParams();

    const isStringOrNumber = (value: unknown): value is string | number =>
      typeof value === "string" || typeof value === "number";

    const possibleFilters: Partial<Record<keyof LlmRunFilters, unknown>> = {
      workflow: llmRun.workflow_id,
      document: llmRun.links?.document_id,
      issue: llmRun.links?.issue_id,
      project: llmRun.links?.project_id,
      clausepart: llmRun.links?.clausepart_id,
      parent: llmRun.links?.parent_run_id,
      batch: llmRun.data.data_store.meta.batchId,
      user: llmRun.username,
    };

    Object.entries(possibleFilters).forEach(([key, value]) => {
      if (
        isStringOrNumber(value) &&
        llmRunFilterKeys.includes(key as keyof LlmRunFilters)
      ) {
        params.append(key, value.toString());
      }
    });

    const creationTime = new Date(llmRun.creation_date).getTime();
    const day = 24 * 60 * 60 * 1000;

    params.append(
      "from",
      formatDateToLocalISOString(new Date(creationTime - day)),
    );
    params.append(
      "to",
      formatDateToLocalISOString(new Date(creationTime + day)),
    );

    return `${baseUrl}?${params.toString()}`;
  };

  return (
    <Container>
      <Header>
        <TitleSection>
          <h1>Workflow</h1>
          <WorkflowName>
            <PlainLink
              to={`/organisation/${organisationId}/workflow/${llmRun.data.workflow_id}/detail`}
            >
              {llmRun.workflow_name}
            </PlainLink>
          </WorkflowName>
        </TitleSection>
        <HeaderRight>
          <FormControlLabel
            control={
              <Checkbox
                checked={isDeveloperMode}
                onChange={onDeveloperModeToggle}
                color="primary"
              />
            }
            label="Dev mode"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showInternalActions}
                onChange={onInternalActionsToggle}
                color="primary"
              />
            }
            label="Show internal actions"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={showActionDetail}
                onChange={onShowActionDetailToggle}
                color="primary"
              />
            }
            label="Show action detail"
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={autoRefreshEnabled}
                onChange={onAutoRefreshToggle}
                color="primary"
              />
            }
            label="Enable auto refresh"
          />
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: ".5em",
              width: "120px",
            }}
          >
            <Button variant="contained" color="primary" onClick={fetchLlmRun}>
              Refresh
            </Button>
            {isRefreshing && <CircularProgress size={20} />}
          </div>

          <BackLink to={location => getBackLinkUrl(location)}>Back</BackLink>
          <IconButton
            ref={anchorRef}
            onClick={handleMenuClick}
            aria-controls={menuOpen ? "menu-list-grow" : undefined}
            aria-haspopup="true"
          >
            <MoreVertIcon />
          </IconButton>
          <Menu
            id="menu-list-grow"
            anchorEl={anchorRef.current}
            open={menuOpen}
            onClose={handleMenuClose}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <MenuItem onClick={handleRerunLlmRun}>Rerun LLM Run</MenuItem>
            {isDeveloperMode && (
              <MenuItem onClick={handleRerunTests}>Rerun Tests</MenuItem>
            )}
          </Menu>
        </HeaderRight>
      </Header>
      <Summary llmRun={llmRun} organisationId={organisationId} />
      {llmRun.data?.errors?.length > 0 && <ErrorSummary llmRun={llmRun} />}
      <Context organisationId={organisationId} llmRun={llmRun} />
      <Inputs llmRun={llmRun} />
      <Variables llmRun={llmRun} />

      <OutputWithTests
        llmRun={llmRun}
        organisationId={organisationId}
        dataKey="output"
        title="Output"
        isDeveloperMode={isDeveloperMode}
        groupedResults={llmRun.grouped_results}
        onAddTest={onAddTest}
        onUpdateTest={onUpdateTest}
        onDeleteTest={onDeleteTest}
        getTestResults={getTestResults}
      />

      <OutputWithTests
        llmRun={llmRun}
        organisationId={organisationId}
        dataKey="results"
        title="Results"
        isDeveloperMode={isDeveloperMode}
        groupedResults={llmRun.grouped_results}
        onAddTest={onAddTest}
        onUpdateTest={onUpdateTest}
        onDeleteTest={onDeleteTest}
        getTestResults={getTestResults}
      />

      <Actions
        llmRun={llmRun}
        actionDefinitions={actionDefinitions}
        prompts={prompts}
        showInternalActions={showInternalActions}
        showActionDetail={showActionDetail}
        organisationId={organisationId}
      />
    </Container>
  );
}
